// =============================================================================
// Transportes CGMG — non-text content for the landing page.
// Facts sourced from "CURRÍCULUM CGMG ESP 2026.pdf" (company corporate CV).
//
// Everything a visitor READS lives in src/i18n/locales/{es,en}.json and is
// joined to the data below by `id` in src/i18n/useContent.js. This file only
// holds what does not change with the language: ids, icons, images, numbers,
// coordinates, addresses and proper names.
// =============================================================================

import flotaKenworthSierra from "../assets/photos/flota-kenworth-sierra.jpg";
import flotaPatio from "../assets/photos/flota-patio.jpg";
import flotaDuo from "../assets/photos/flota-duo.jpg";
import tallerInterior from "../assets/photos/taller-interior.jpg";
import patio1 from "../assets/photos/patio-1.jpg";
import { NAP, mapsHref } from "../seo/site";

// Client logos live in public/clients/ (not imported as modules) — one of
// the source SVGs carries editor namespace metadata that crashes CRA's
// SVG-to-component (SVGR) pipeline on import; serving them as plain static
// files sidesteps that entirely since the browser just renders them as-is.
const clientLogo = (file) => `${process.env.PUBLIC_URL}/clients/${file}`;

export const site = {
  name: "Transportes CGMG",
  legalFounder: "Carlos Gerardo Martínez Gómez",
  foundedPartner: 1993,
  foundedLegal: 1999,
  yearsExperience: 33,
  domain: "transportes-cgmg.com",
  primaryEmail: "contacto@transportescgmg.com",
  primaryPhoneDisplay: "639 474 2183",
};

// ids double as the section anchors (#inicio, #nosotros, ...) and as the keys
// under `nav.*` in the locale files. The anchors stay the same in every
// language so shared links keep working.
export const navIds = ["inicio", "nosotros", "servicios", "cobertura", "flota", "clientes", "contacto"];

export const heroBadgeIds = ["experience", "fleet", "gps"];

export const aboutParagraphIds = ["story", "today"];

export const objectiveIds = ["personalized", "improvement", "delivery"];

export const advantageList = [
  { id: "training", icon: "training" },
  { id: "commitment", icon: "handshake" },
  { id: "storage", icon: "warehouse" },
  { id: "gps", icon: "gps" },
  { id: "price", icon: "price" },
  { id: "reports", icon: "report" },
  { id: "insurance", icon: "shield" },
];

// Derived from fleet composition + advantages + objectives (not a literal
// "servicios" list in the source document).
export const serviceList = [
  { id: "dry", icon: "box" },
  { id: "reefer", icon: "snow" },
  { id: "storage", icon: "warehouse2" },
  { id: "tracking", icon: "radar" },
  { id: "routes", icon: "route" },
];

export const fleetStatList = [
  { id: "tractors", value: 26, suffix: "" },
  { id: "dryVans", value: 40, suffix: "" },
  { id: "reefer", value: 1, suffix: "" },
  { id: "years", value: 33, suffix: "+" },
];

export const fleetLocationList = [
  {
    id: "workshop",
    icon: "building",
    address: "Calle 23 Norte #404, Sector Norte, Cd. Delicias, Chih.",
  },
];

export const galleryList = [
  { id: "kenworthSierra", src: flotaKenworthSierra, big: true },
  { id: "duo", src: flotaDuo },
  { id: "workshop", src: tallerInterior },
  { id: "yard1", src: patio1 },
  { id: "yard2", src: flotaPatio },
];

// Place names are proper nouns: identical in both languages.
export const coverageData = {
  states: [
    "Chihuahua",
    "Aguascalientes",
    "Colima",
    "Durango",
    "Guanajuato",
    "Jalisco",
    "Nuevo León",
    "Querétaro",
    "San Luis Potosí",
    "Tamaulipas",
    "Zacatecas",
  ],
  ports: ["Altamira", "Manzanillo"],
};

export const clientList = [
  { name: "LALA", logo: clientLogo("lala.svg") },
  { name: "Alpura", logo: clientLogo("alpura.png"), big: true },
  { name: "Tristone Flowtech", logo: clientLogo("tristone-flowtech.png"), big: true },
  { name: "Grupo Cimarrón", logo: clientLogo("grupo-cimarron.png"), big: true },
  { name: "Operadora Marta", logo: clientLogo("operadora-marta.png"), big: true },
  { name: "Conservas La Costeña", logo: clientLogo("conservas-la-costena.svg") },
  { name: "Grupo La Italiana", logo: clientLogo("grupo-la-italiana.png"), big: true },
  { name: "Solistica", logo: clientLogo("solistica.png"), big: true },
  { name: "Crown Holdings", logo: clientLogo("crown-holdings.svg") },
];

// Name, address and phone (NAP) come from one place (src/seo/site.js) so they
// are spelled identically in the contact section, the footer, the city and
// service pages and the schema.org data.
export const contactData = {
  email: NAP.email,
  office: {
    address: NAP.addressLine,
    mapsHref,
  },
};
